import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaBan, FaCheckCircle, FaClock, FaDog, FaSmoking, 
  FaVolumeUp, FaUsers, FaChild 
} from 'react-icons/fa';
import { MdOutlineRule } from 'react-icons/md';

const HouseRules = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const rules = {
    allowed: [
      { icon: <FaCheckCircle />, text: "Check in 11:00 AM | Checkout 10:00 AM" },
      { icon: <FaCheckCircle />, text: "Families and married couples are welcome" },
      { icon: <FaCheckCircle />, text: "Valid ID proof required at check-in" },
      { icon: <FaCheckCircle />, text: "Early check-in available (Subject to availability)" },
    ],
    notAllowed: [
      { icon: <FaBan />, text: "No smoking permitted inside the premises" },
      { icon: <FaBan />, text: "No pets allowed" },
      { icon: <FaBan />, text: "Quiet hours between - 10:00 PM to 8:00 AM" },
      { icon: <FaBan />, text: "No loud music" },
      { icon: <FaBan />, text: "We do not host unmarried couples" },
      { icon: <FaBan />, text: "No parties or events" },
    ]
  };

  const guidelines = [
    { icon: <FaUsers />, title: "Guest Policy", desc: "Maximum occupancy as per room type" },
    { icon: <FaVolumeUp />, title: "Quiet Hours", desc: "10 PM - 8 AM for peaceful environment" },
    { icon: <FaChild />, title: "Child Safety", desc: "Parents responsible for children's safety" },
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-white to-primary-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ rotate: [0, 5, -5, 5, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <MdOutlineRule className="text-primary-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            House Rules & Guidelines
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Please review our policies to ensure a pleasant stay for everyone
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Allowed */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-green-500 text-white p-3 rounded-xl">
                <FaCheckCircle className="text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">What's Allowed</h3>
            </div>
            <ul className="space-y-4">
              {rules.allowed.map((rule, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-start space-x-3 text-gray-700"
                >
                  <span className="text-green-500 text-xl mt-1">{rule.icon}</span>
                  <span>{rule.text}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Not Allowed */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-red-500 text-white p-3 rounded-xl">
                <FaBan className="text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Not Allowed</h3>
            </div>
            <ul className="space-y-4">
              {rules.notAllowed.map((rule, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-start space-x-3 text-gray-700"
                >
                  <span className="text-red-500 text-xl mt-1">{rule.icon}</span>
                  <span>{rule.text}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Guidelines Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {guidelines.map((guideline, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 + index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow text-center"
            >
              <div className="text-primary-500 text-4xl mb-3 flex justify-center">
                {guideline.icon}
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{guideline.title}</h4>
              <p className="text-sm text-gray-600">{guideline.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Important Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.9 }}
          className="mt-12 bg-gradient-to-r from-primary-500 to-nature-leaf text-white rounded-2xl p-8 text-center"
        >
          <p className="text-lg mb-2">
            <strong>Important:</strong> We reserve the right to cancel bookings if guests don't comply with house rules.
          </p>
          <p className="text-sm opacity-90">
            Questions about our policies? Feel free to contact us anytime!
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HouseRules;
